import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { APPEARANCE_PRESETS, DEFAULT_APPEARANCE, useAppearance, type Appearance } from '@/stores/appearance';
import { Icon } from '@/components/Icon';
import s from './AppearancePage.module.css';

const RANGES: { key: keyof Appearance; label: string; min: number; max: number }[] = [
  { key: 'fontSize', label: '字号', min: 12, max: 18 },
  { key: 'lineHeight', label: '行高', min: 18, max: 30 },
  { key: 'gap', label: '间距', min: 6, max: 18 },
];

export function AppearanceEditor({ sheet = false, onClose }: { sheet?: boolean; onClose: () => void }) {
  const ff = useFormFactor();
  const user = useSession((state) => state.user);
  const value = useAppearance((state) => state.value);
  const loaded = useAppearance((state) => state.loaded);
  const setValue = useAppearance((state) => state.setValue);
  const save = useAppearance((state) => state.save);
  const [status, setStatus] = useState('');
  const tabletSheet = sheet && ff === 'tablet';

  useEffect(() => {
    if (!sheet) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet, onClose]);

  const commit = async (next: Appearance) => {
    if (!user) return;
    setValue(next);
    setStatus('保存中…');
    const result = await save(user.id, next);
    setStatus(result === 'synced' ? '已同步到当前账号' : '已保存在这台设备；服务端只读或暂不可用');
  };

  const active = APPEARANCE_PRESETS.find((preset) =>
    preset.value.fontSize === value.fontSize && preset.value.lineHeight === value.lineHeight && preset.value.gap === value.gap)?.key;

  return <div className={`${s.page} ${sheet ? s.sheet : ''} ${tabletSheet ? s.tabletSheet : ''}`} role={sheet ? 'dialog' : undefined} aria-modal={sheet ? true : undefined} aria-label={sheet ? '外观与密度' : undefined}>
    <header className={s.header}>
      {tabletSheet ? <h1 className={s.sheetTitle}>外观与密度<small>只对你的账号生效</small></h1> : <>
        <button type="button" className={s.back} onClick={onClose}>{sheet ? '取消' : '‹ 我的'}</button>
        <h1>外观与密度</h1>
        <button type="button" className={s.reset} disabled={!loaded} onClick={() => void commit(DEFAULT_APPEARANCE)}>重置</button>
      </>}
      {tabletSheet && <button type="button" className={s.sheetClose} onClick={onClose} aria-label="关闭"><Icon name="x" size={20} /></button>}
    </header>
    <div className={s.body}>
      <h2 className={tabletSheet ? s.sectionHeading : undefined}>预览</h2>
      {ff === 'phone' ? <section className={s.phonePreview} aria-label="订单列表预览">
        <div className={s.previewTitle}><span>订单 · 我的待办</span><span>手机正文 {value.fontSize + 1} px</span></div>
        {[
          { number: 'XS20260921003', name: '南通盛辉家居', state: '组装中', tone: s.previewAssembling },
          { number: 'XS20260918007', name: '宁波辉盛日用品', state: '配货齐全', tone: s.previewReady },
          { number: 'XS20260915004', name: '苏州明泰五金', state: '部分发货', tone: s.previewPartial },
        ].map(({ number, name, state, tone }) => <div key={number} className={s.previewRow} style={{ fontSize: value.fontSize + 1, lineHeight: `${value.lineHeight + 1}px`, paddingInline: value.gap + 2 }}>
          <b>{number}</b><span>{name}</span><em className={tone}>{state}</em>
        </div>)}
      </section> : <section className={s.tablePreview} aria-label="列表行高预览">
        <div className={s.previewTitle}><span>{tabletSheet ? '订单号 · 客户 · 状态' : '订单 · 我的待办'}</span><span>{tabletSheet ? `行高 ${value.lineHeight * 2 + 14} = ${value.lineHeight} × 2 + 14` : `行高 ${value.lineHeight * 2 + 14} px`}</span></div>
        {!tabletSheet && <div className={s.tableHead}><span>订单号</span><span>客户</span><span>状态</span></div>}
        <div className={s.tableRow} style={{ height: value.lineHeight * 2 + 14, fontSize: value.fontSize, lineHeight: `${value.lineHeight}px`, paddingInline: value.gap + 4 }}><b>XS20260921003</b><span>南通盛辉家居</span><em>组装中</em></div>
        {tabletSheet && <div className={s.tableRow} style={{ height: value.lineHeight * 2 + 14, fontSize: value.fontSize, lineHeight: `${value.lineHeight}px`, paddingInline: value.gap + 4 }}><b>XS20260918007</b><span>宁波辉盛日用品</span><em className={s.previewReady}>配货齐全</em></div>}
      </section>}

      <h2 className={tabletSheet ? s.sectionHeading : undefined}>预设</h2>
      <div className={s.presets}>{APPEARANCE_PRESETS.map((preset) => <button key={preset.key} type="button" disabled={!loaded} className={active === preset.key ? s.active : ''} aria-pressed={active === preset.key} onClick={() => void commit(preset.value)}>
        {preset.label}<small>{preset.value.fontSize}/{preset.value.lineHeight}/{preset.value.gap}</small>
      </button>)}</div>

      <h2 className={tabletSheet ? s.sectionHeading : undefined}>细调 <small>拖动即时生效</small></h2>
      <div className={s.ranges}>{RANGES.map(({ key, label, min, max }) => <label className={s.range} key={key}>
        <span>{label}<small>{min}–{max}</small></span>
        <input type="range" min={min} max={max} step="1" disabled={!loaded} value={value[key]} aria-label={label}
          style={tabletSheet ? { background: `linear-gradient(to right, var(--blue) ${(value[key] - min) / (max - min) * 100}%, var(--line) ${(value[key] - min) / (max - min) * 100}%) center / 100% 4px no-repeat` } : undefined}
          onChange={(event) => { setStatus('预览中…'); setValue({ ...value, [key]: Number(event.target.value) }); }}
          onPointerUp={(event) => void commit({ ...useAppearance.getState().value, [key]: Number(event.currentTarget.value) })}
          onKeyUp={(event) => void commit({ ...useAppearance.getState().value, [key]: Number(event.currentTarget.value) })}
          onBlur={(event) => { if (status === '预览中…') void commit({ ...useAppearance.getState().value, [key]: Number(event.currentTarget.value) }); }} />
        <strong>{value[key]}<small>px</small></strong>
      </label>)}</div>
      <p className={s.help}>{tabletSheet ? 'iPad 与电脑用同一组数值，手机正文自动大 1。按钮和可点区域始终不小于 44，只放大文字和间距。' : '手机正文比这里的字号大 1（标准 14 → 手机 15），iPad 和电脑按原值。按钮和可点区域始终不小于 44。'}</p>
      {status && <p className={s.status} role="status">{status}</p>}
    </div>
    {sheet && <footer className={`${s.footer} ${tabletSheet ? s.tabletFooter : ''}`}><span>只对你的账号生效</span><button type="button" disabled={!loaded} onClick={() => void commit(DEFAULT_APPEARANCE)}>恢复默认</button><button type="button" className={s.done} onClick={onClose}>完成</button></footer>}
  </div>;
}

export function AppearancePage() {
  const navigate = useNavigate();
  return <AppearanceEditor onClose={() => navigate('/me')} />;
}
